import React from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { Dismiss20Regular, Search20Regular } from "@fluentui/react-icons";
// SP1 (Mail-Topbar): eigene Such-Komponente in der warmen Chat-Palette —
// bewusst KEIN Fluent-<Input>/<Spinner>; nur Fluent-ICONS sind erlaubt.
import { AUB, INK, INK2, LINE } from "../chat/chatTheme";

const useStyles = makeStyles({
    root: {
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        boxSizing: "border-box",
        height: "32px",
        ...shorthands.padding("0", "10px"),
        ...shorthands.border("1px", "solid", LINE),
        ...shorthands.borderRadius("8px"),
        backgroundColor: "#ffffff",
        transitionProperty: "border-color, box-shadow",
        transitionDuration: "0.15s",
        ":focus-within": {
            ...shorthands.borderColor(AUB),
            boxShadow: `0 0 0 1px ${AUB}`,
        },
    },
    /* Touch-Variante (Phone): höheres Trefferziel. */
    rootLarge: {
        height: "40px",
    },
    searchIcon: {
        color: INK2,
        flexShrink: 0,
    },
    input: {
        flexGrow: 1,
        minWidth: 0,
        ...shorthands.border("0px", "none"),
        outlineStyle: "none",
        backgroundColor: "transparent",
        color: INK,
        fontSize: "13px",
        fontFamily: "inherit",
        ...shorthands.padding("0"),
        "::placeholder": {
            color: INK2,
        },
    },
    clearBtn: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        width: "22px",
        height: "22px",
        ...shorthands.padding("0"),
        ...shorthands.border("0px", "none"),
        ...shorthands.borderRadius("6px"),
        backgroundColor: "transparent",
        color: INK2,
        cursor: "pointer",
        ":hover": {
            backgroundColor: LINE,
            color: INK,
        },
    },
    /* Kleiner Lade-Ring (eigenes Element statt Fluent-<Spinner>). */
    spinner: {
        width: "14px",
        height: "14px",
        flexShrink: 0,
        boxSizing: "border-box",
        ...shorthands.borderRadius("50%"),
        ...shorthands.border("2px", "solid", LINE),
        borderTopColor: AUB,
        animationName: { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } },
        animationDuration: "0.8s",
        animationIterationCount: "infinite",
        animationTimingFunction: "linear",
    },
});

export type MailSearchInputProps = {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    /** true, während die Suche läuft — zeigt den Lade-Ring statt des Löschen-Buttons. */
    busy?: boolean;
    /** "large" = höhere Touch-Variante (Phone). */
    size?: "medium" | "large";
    className?: string;
    style?: React.CSSProperties;
    /**
     * Zusätzlicher Key-Handler auf dem <input> (läuft VOR dem internen
     * Escape-Leeren). Erlaubt dem Aufrufer z. B., Escape bei aktiver Suche per
     * stopPropagation vom Umgebungs-Handler (Dialog schließen) abzuschirmen.
     */
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

/**
 * SP1 (Mail-Topbar): gestyltes natives <input> mit Such-/Löschen-Icon in der
 * warmen Chat-Palette — ersetzt das Fluent-<Input> der alten Suchleiste über
 * der Liste. ESC oder der Löschen-Button leeren die Suche.
 */
export const MailSearchInput: React.FC<MailSearchInputProps> = ({
    value,
    onChange,
    placeholder = "E-Mails durchsuchen …",
    busy = false,
    size = "medium",
    className,
    style,
    onKeyDown,
}) => {
    const styles = useStyles();
    return (
        <div
            className={mergeClasses(styles.root, size === "large" && styles.rootLarge, className)}
            style={style}
        >
            <Search20Regular className={styles.searchIcon} />
            <input
                className={styles.input}
                type="text"
                value={value}
                placeholder={placeholder}
                aria-label={placeholder}
                autoComplete="off"
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={(e) => {
                    onKeyDown?.(e);
                    if (e.key === "Escape") onChange("");
                }}
            />
            {busy ? (
                <span className={styles.spinner} role="status" aria-label="Suche läuft" />
            ) : value ? (
                <button
                    type="button"
                    className={styles.clearBtn}
                    aria-label="Suche löschen"
                    onClick={() => onChange("")}
                >
                    <Dismiss20Regular />
                </button>
            ) : null}
        </div>
    );
};
